import { BehaviorSubject, combineLatest, map, Observable, of } from "rxjs";
import { IMenuBadgeAlert } from "core-client/api";

/** Colour of a rendered badge: 'default' is the red count badge it has always been. */
export type MenuBadgeTone = "default" | "warning" | "critical";

/** What the header renders for one item's badge; null renders nothing. */
export interface MenuBadgeView {
  readonly text: string;
  readonly tone: MenuBadgeTone;
  /** Status line on rows and suffix of an icon's accessible name; null without an alert. */
  readonly message: string | null;
}

/**
 * Combines an item's count with its alert. A critical alert replaces the count with "!";
 * a warning keeps the count (or "!" without one) and only changes the colour.
 */
export function toMenuBadgeView(
  count: number | null | undefined,
  alert: IMenuBadgeAlert | null | undefined,
): MenuBadgeView | null {
  const hasCount = typeof count === "number" && count > 0;
  if (alert?.severity === "critical") {
    return { text: "!", tone: "critical", message: alert.message };
  }
  if (alert?.severity === "warning") {
    return { text: hasCount ? String(count) : "!", tone: "warning", message: alert.message };
  }
  return hasCount ? { text: String(count), tone: "default", message: null } : null;
}

/** The most urgent of several alerts (critical before warning), e.g. for the hamburger badge. */
export function worstMenuBadgeAlert(
  alerts: ReadonlyArray<IMenuBadgeAlert | null | undefined>,
): IMenuBadgeAlert | null {
  return (
    alerts.find((a) => a?.severity === "critical") ??
    alerts.find((a) => a?.severity === "warning") ??
    null
  );
}

/** Live badge view of a menu or dropdown item. */
export function menuBadgeView$(item: {
  readonly badge$?: BehaviorSubject<number>;
  readonly badgeAlert$?: BehaviorSubject<IMenuBadgeAlert | null>;
}): Observable<MenuBadgeView | null> {
  if (!item.badge$ && !item.badgeAlert$) {
    return of(null);
  }
  const count$: Observable<number> = item.badge$ ?? of(0);
  const alert$: Observable<IMenuBadgeAlert | null> = item.badgeAlert$ ?? of(null);
  return combineLatest([count$, alert$]).pipe(map(([count, alert]) => toMenuBadgeView(count, alert)));
}
